import {
  Button,
  Label,
  Stack,
  TextField,
  Text,
  Card,
  Divider,
} from "@towersystems/roam-common-native";
import { Controller, useFormContext } from "react-hook-form";
import { Spacings, View } from "react-native-ui-lib";
import { InvoiceArrivalItemFormValues } from "./types";
import { InvoiceArrivalItemFormProvider } from "./InvoiceArrivalItemFormProvider";
import { AntDesign, MaterialCommunityIcons } from "@expo/vector-icons";
import React from "react";
import { SerialNumbersInput } from "./components";
import { moneyToFormattedString } from "@towersystems/roam-common/lib/shared-utils";
import { MarkupStyle } from "@towersystems/roam-common/lib/generated-types";
import { useTranslation } from "../../../../utilities/i18n";

export interface InvoiceArrivalItemFormProps {
  showSerialNumbers?: boolean;
}

export const InvoiceArrivalItemForm = ({
  showSerialNumbers,
}: InvoiceArrivalItemFormProps) => {
  const { t } = useTranslation("invoice_arrival");
  const { formState, control, getValues, watch, resetField, setValue } =
    useFormContext<InvoiceArrivalItemFormValues>();
  const { isValid, isSubmitting, errors, touchedFields } = formState;

  const handleOnCalculateCostPrice = React.useCallback(() => {
    const { costPrice, quantity } = getValues();

    if (!isValid || parseFloat(quantity) <= 0 || parseFloat(costPrice) <= 0) {
      return;
    }

    const newCostPrice = (parseFloat(costPrice) / parseFloat(quantity)).toFixed(
      4
    );
    setValue("costPrice", newCostPrice, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  }, [isValid]);

  const recalculateTradeOrWebPrice = React.useCallback(
    (field: "webPrice" | "tradePrice", force = true) => {
      if (!(force || !touchedFields[field])) {
        return;
      }

      const markup =
        field == "tradePrice"
          ? getValues("tradeMarkup")
          : getValues("webMarkup");

      let newPrice = parseFloat(
        field == "tradePrice"
          ? getValues("retailPrice")
          : getValues("tradePrice")
      );

      switch (markup?.markupStyle) {
        case MarkupStyle.ABOVE_COST:
          const costPrice = parseFloat(getValues("costPrice"));
          newPrice = costPrice + (costPrice * markup?.markupPercentage) / 100;
          break;
        case MarkupStyle.OFF_RETAIL:
          const retailPrice = parseFloat(getValues("retailPrice"));
          newPrice =
            retailPrice - (retailPrice * markup?.markupPercentage) / 100;
          break;
      }

      if (isNaN(newPrice)) {
        return;
      }
      resetField(field, {
        defaultValue: newPrice.toFixed(2).toString(),
        keepDirty: true,
        keepTouched: false,
        keepError: true,
      });
    },
    [touchedFields.tradePrice]
  );

  const watchedProductId = watch("productId");
  const watchedSerialNumbers = watch("serialNumbers", []);
  const watchedQuantity = watch("quantity", "0");
  const watchedCostPrice = watch("costPrice", "0");
  const watchedRetailMarkup = watch("retailMarkup");
  const watchedTradeMarkup = watch("tradeMarkup");
  const watchedWebMarkup = watch("webMarkup");

  React.useEffect(() => {
    // Calculate any default prices if they are not set, using the cost price
    const webPrice = getValues("webPrice");
    const tradePrice = getValues("tradePrice");
    const retailPrice = getValues("retailPrice");

    if (!webPrice || webPrice === "0") {
      recalculateTradeOrWebPrice("webPrice", false);
    }

    if (!tradePrice || tradePrice === "0") {
      recalculateTradeOrWebPrice("tradePrice", false);
    }

    if (!retailPrice || retailPrice === "0") {
      recalculateRetailPrice(false);
    }
  }, []);

  const recalculateRetailPrice = React.useCallback(
    (force: boolean) => {
      if (!force && touchedFields.retailPrice) {
        return;
      }
      const costPrice = getValues("costPrice");
      const retailMarkup = getValues("retailMarkup");
      const markupPercent = retailMarkup?.markupPercentage ?? 0;

      const retailPrice =
        markupPercent > 0
          ? parseFloat(costPrice) * (1 + markupPercent / 100)
          : parseFloat(costPrice);

      if (isNaN(retailPrice)) {
        return;
      }

      resetField("retailPrice", {
        defaultValue: retailPrice.toFixed(2).toString(),
        keepDirty: true,
        keepTouched: false,
        keepError: true,
      });
    },
    [touchedFields]
  );

  const barcodeMarkup = !watchedProductId ? (
    <View>
      <Label required label={t("form.item_barcode.label")}></Label>
      <Controller
        name="barcode"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <TextField
            value={value}
            onChangeText={onChange}
            validationMessage={
              error ? error.message : errors.root?.barcode?.message ?? undefined
            }
            placeholder={t("form.item_barcode.placeholder")}
          />
        )}
      />
    </View>
  ) : null;

  const nameMarkup = !watchedProductId ? (
    <View>
      <Label label={t("form.item_name.label")}></Label>
      <Controller
        name="name"
        disabled={false}
        control={control}
        render={({ field: { onChange, value } }) => (
          <TextField value={value} editable={true} onChangeText={onChange} />
        )}
      />
    </View>
  ) : null;

  const serialnumberMarkup =
    !watchedProductId ||
    showSerialNumbers ||
    watchedSerialNumbers.length >= 1 ? (
      <Card title={t("card.item_serial_numbers.title")}>
        <SerialNumbersInput />
      </Card>
    ) : null;

  const totalCostMarkup = React.useMemo(() => {
    const totalCost = isValid
      ? parseFloat(watchedQuantity) * parseFloat(watchedCostPrice)
      : undefined;
    return (
      <View style={{ alignItems: "flex-end" }}>
        <Text fontWeight="bold" variant="heading1">
          {t("item_total_cost", {
            totalCost: moneyToFormattedString(totalCost),
          })}
        </Text>
      </View>
    );
  }, [isValid, watchedQuantity, watchedCostPrice]);

  return (
    <View>
      <Stack spacing={Spacings.s5} vertical>
        <Card title={t("card.details.title")}>
          <Stack spacing={Spacings.s5} vertical>
            {barcodeMarkup}
            {nameMarkup}
            <View>
              <Label label={t("form.item_quantity.label")}></Label>
              <Controller
                name="quantity"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    value={value}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.item_quantity.placeholder")}
                    keyboardType="numeric"
                  />
                )}
              />
            </View>
            <View>
              <Label label={t("form.item_cost_price.label")}></Label>
              <Controller
                name="costPrice"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => {
                  return (
                    <TextField
                      value={value ?? "0"}
                      onChangeText={(text) => {
                        onChange(text, {
                          shouldTouch: true,
                        });
                        recalculateRetailPrice(false);
                        recalculateTradeOrWebPrice("tradePrice", false);
                        recalculateTradeOrWebPrice("webPrice", false);
                      }}
                      validationMessage={error ? error.message : undefined}
                      keyboardType="numeric"
                      formatterType={
                        errors.costPrice?.message ? undefined : "money"
                      }
                      trailingAccessory={
                        <Button
                          size="compact"
                          disabled={!isValid || isSubmitting}
                          onPress={handleOnCalculateCostPrice}
                        >
                          <Text color="action">
                            <MaterialCommunityIcons
                              name="package-variant"
                              size={24}
                            />
                          </Text>
                        </Button>
                      }
                    />
                  );
                }}
              />
            </View>
            <View>
              <Label label={t("form.item_retail_price.label")}>
                {watchedRetailMarkup?.markupPercentage ? (
                  <Text>
                    {t("inc_perc_markup", {
                      markup: watchedRetailMarkup?.markupPercentage ?? 0,
                    })}
                  </Text>
                ) : undefined}
              </Label>
              <Controller
                name="retailPrice"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? 0}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    keyboardType="numeric"
                    formatterType={
                      errors.retailPrice?.message ? undefined : "money"
                    }
                    trailingAccessory={
                      <Button
                        size="compact"
                        onPress={() => recalculateRetailPrice(true)}
                        isAction
                        disabled={!touchedFields?.retailPrice || !isValid}
                      >
                        <Text color="action">
                          <AntDesign name="calculator" size={24} />
                        </Text>
                      </Button>
                    }
                  />
                )}
              />
            </View>
            {
              <View>
                <Label label={t("form.item_trade_price.label")}>
                  {watchedTradeMarkup?.markupPercentage ? (
                    <Text>{` (Inc ${watchedTradeMarkup?.markupPercentage}% Markup, ${watchedTradeMarkup.markupStyle})`}</Text>
                  ) : undefined}
                </Label>
                <Controller
                  name="tradePrice"
                  control={control}
                  render={({
                    field: { onChange, value, onBlur },
                    fieldState: { error },
                  }) => (
                    <TextField
                      onBlur={onBlur}
                      value={value ?? 0}
                      onChangeText={onChange}
                      validationMessage={error ? error.message : undefined}
                      keyboardType="numeric"
                      formatterType={
                        errors.tradePrice?.message ? undefined : "money"
                      }
                      trailingAccessory={
                        <Button
                          size="compact"
                          onPress={() =>
                            recalculateTradeOrWebPrice("tradePrice", true)
                          }
                          isAction
                          disabled={!touchedFields?.tradePrice || !isValid}
                        >
                          <Text color="action">
                            <AntDesign name="calculator" size={24} />
                          </Text>
                        </Button>
                      }
                    />
                  )}
                />
              </View>
            }
            {
              <View>
                <Label label={t("form.item_web_price.label")}>
                  {watchedWebMarkup?.markupPercentage ? (
                    <Text>
                      {t("inc_perc_markup2", {
                        markup: watchedWebMarkup?.markupPercentage ?? 0,
                        markupStyle: watchedWebMarkup.markupStyle,
                      })}
                    </Text>
                  ) : undefined}
                </Label>
                <Controller
                  name="webPrice"
                  control={control}
                  render={({
                    field: { onChange, value, onBlur },
                    fieldState: { error },
                  }) => (
                    <TextField
                      onBlur={onBlur}
                      value={value ?? 0}
                      onChangeText={onChange}
                      validationMessage={error ? error.message : undefined}
                      keyboardType="numeric"
                      formatterType={
                        errors.tradePrice?.message ? undefined : "money"
                      }
                      trailingAccessory={
                        <Button
                          size="compact"
                          onPress={() =>
                            recalculateTradeOrWebPrice("webPrice", true)
                          }
                          isAction
                          disabled={!touchedFields?.webPrice || !isValid}
                        >
                          <Text color="action">
                            <AntDesign name="calculator" size={24} />
                          </Text>
                        </Button>
                      }
                    />
                  )}
                />
              </View>
            }
          </Stack>
        </Card>
        {totalCostMarkup}
        <Divider />
        {serialnumberMarkup}
      </Stack>
    </View>
  );
};

InvoiceArrivalItemForm.Provider = InvoiceArrivalItemFormProvider;
